import { BodyExtraSmall, BodySmall, Stepper, Step } from '@taxbit-private/cosmic'
import {
  COSMIC_VALUE_PLACEHOLDER,
  useCosmicLocalizationContext,
} from '@taxbit-private/cosmic-localization'
import { useMemo } from 'react'
import styled from 'styled-components'

import { PendingChangesResponse } from 'api/types/pendingChangesApiTypes'
import { useOrgsByParent } from 'hooks/data/orgsHook'

import {
  getActiveChildOrgPendingChanges,
  getActiveParentPendingChanges,
  type ProcessedPendingChange,
} from './dataStatusUtils'

const LegalEntityName = styled.span`
  font-size: ${({ theme }) => theme.fontSize.s};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${({ theme }) => theme.color.textSecondary};
  display: inline;
`

type WarningStateStepperProps = {
  lastSuccessfulUpdate?: string | null
  nextUpdateScheduled?: string | null
  pendingChangesData?: PendingChangesResponse
}

const WarningStateStepper = ({
  lastSuccessfulUpdate,
  nextUpdateScheduled,
  pendingChangesData,
}: WarningStateStepperProps) => {
  const { formatDateTime } = useCosmicLocalizationContext()
  const { data: orgsByParent } = useOrgsByParent()

  const childOrgDisplayNameBySlug = useMemo(() => {
    const displayNameBySlug = new Map<string, string>()

    for (const parentOrg of orgsByParent ?? []) {
      for (const childOrg of parentOrg.children) {
        if (!childOrg.org_slug) {
          continue
        }

        displayNameBySlug.set(childOrg.org_slug, childOrg.org_display_name ?? childOrg.org_name)
      }
    }

    return displayNameBySlug
  }, [orgsByParent])

  const parentChanges = useMemo(
    () =>
      getActiveParentPendingChanges(
        pendingChangesData?.pending_changes,
        pendingChangesData?.last_successful_valid_dag_run_start_time
      ),
    [
      pendingChangesData?.pending_changes,
      pendingChangesData?.last_successful_valid_dag_run_start_time,
    ]
  )

  const childChanges = useMemo(
    () => getActiveChildOrgPendingChanges(pendingChangesData?.children, childOrgDisplayNameBySlug),
    [pendingChangesData?.children, childOrgDisplayNameBySlug]
  )

  const steps: Step[] = useMemo(() => {
    const allChanges = [...parentChanges, ...childChanges].sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    )

    const formattedTimestamp = (timestamp: string) => (
      <BodyExtraSmall colorVariant="secondary">
        {formatDateTime({ date: timestamp, format: 'DateTimeShortened', tz: 'Browser' })}
      </BodyExtraSmall>
    )

    const changeSteps: Step[] = allChanges.map((change: ProcessedPendingChange) => ({
      key: change.key,
      title: change.title,
      titleUtilityElement: formattedTimestamp(change.timestamp),
      titleUtilityElementPlacement: 'right' as const,
      content: change.legalEntityDisplayName ? (
        <BodySmall colorVariant="secondary" display="inline">
          In <LegalEntityName>{change.legalEntityDisplayName}</LegalEntityName> legal entity
        </BodySmall>
      ) : (
        <></>
      ),
      avatarProps: { iconName: change.iconName, variant: 'warning' as const },
    }))

    return [
      {
        key: 'last-successful-update',
        title: 'Last successful update',
        titleUtilityElement: (
          <BodyExtraSmall colorVariant="secondary">
            {lastSuccessfulUpdate
              ? formatDateTime({
                  date: lastSuccessfulUpdate,
                  format: 'DateTimeShortened',
                  tz: 'Browser',
                })
              : COSMIC_VALUE_PLACEHOLDER}
          </BodyExtraSmall>
        ),
        titleUtilityElementPlacement: 'right' as const,
        content: <></>,
        avatarProps: { iconName: 'check-circle' as const, variant: 'success' as const },
      },
      ...changeSteps,
      {
        key: 'next-update-scheduled',
        title: 'Next update scheduled',
        titleUtilityElement: (
          <BodyExtraSmall colorVariant="secondary">
            {nextUpdateScheduled
              ? formatDateTime({
                  date: nextUpdateScheduled,
                  format: 'DateTimeShortened',
                  tz: 'Browser',
                })
              : COSMIC_VALUE_PLACEHOLDER}
          </BodyExtraSmall>
        ),
        titleUtilityElementPlacement: 'right' as const,
        content: <></>,
        avatarProps: { iconName: 'clock' as const, variant: 'secondary' as const },
      },
    ]
  }, [parentChanges, childChanges, lastSuccessfulUpdate, nextUpdateScheduled, formatDateTime])

  return <Stepper currentStep={1} steps={steps} />
}

export default WarningStateStepper
